import React, { memo, useCallback } from 'react';
import type { ProductOfferItemVariantProps } from './types';
import { Text } from '../../Text';
import { CheckBox } from '../../CheckBox';
import { Badge } from '../../Badge';
import { Image } from '../../Image';
import { mapProductCatalogStyles, getProductOfferGridStyles } from '../../../../utils/styleMapper';
import { resolveLabel } from '../../../../utils/contentResolver';
import styles from '../ProductOfferItem.module.css';

export const LabelsCheckbox = memo(function LabelsCheckbox({
  item,
  index,
  locale,
  variables,
  selected = false,
  catalogAppearance,
  selectedCatalogAppearance,
  onSelect,
  className,
  style,
  'data-testid': testId,
  editPath,
  previewMode,
  layout = 'vertical',
}: ProductOfferItemVariantProps): React.ReactElement | null {
  const isHorizontal = layout === 'horizontal';

  const handleClick = useCallback(() => {
    if (onSelect) {
      onSelect({
        item,
        index,
        eventType: selected ? 'ListItemDeselect' : 'ListItemSelect',
        productId: item.productId,
      });
    }
  }, [selected, onSelect, item, index]);

  const containerClassName = isHorizontal
    ? [styles.cardItem, selected ? styles.cardSelected : '', className].filter(Boolean).join(' ')
    : [styles.productItem, selected ? styles.selected : '', className].filter(Boolean).join(' ');

  const listMappedStyles = mapProductCatalogStyles(catalogAppearance);
  const selectedMappedStyles = selected ? mapProductCatalogStyles(selectedCatalogAppearance) : {};

  const containerStyle = {
    ...listMappedStyles,
    ...selectedMappedStyles,
    paddingTop: listMappedStyles.paddingTop,
    paddingBottom: listMappedStyles.paddingBottom,
    paddingLeft: listMappedStyles.paddingLeft,
    paddingRight: listMappedStyles.paddingRight,
    ...style,
  };

  const hasStartContent = !!(
    resolveLabel(item.topStartLabel, locale) ||
    resolveLabel(item.bottomStartLabel, locale)
  );
  const hasEndContent = !!(
    resolveLabel(item.topEndLabel, locale) ||
    resolveLabel(item.bottomEndLabel, locale)
  );

  const { gridTemplateColumns, columnGap, rowGap } = getProductOfferGridStyles(
    hasStartContent,
    hasEndContent,
    item.appearance
  );

  const labelsContainerStyle = {
    gridTemplateColumns,
    columnGap: columnGap ? `${columnGap}px` : undefined,
  };

  const columnGapStyle = rowGap !== undefined ? { gap: rowGap } : undefined;
  const labelWrapperStyle = { flex: 1, display: 'flex', alignItems: 'center' } as const;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  if (isHorizontal) {
    return (
      <div
        className={containerClassName}
        style={{ ...containerStyle, justifyContent: 'space-between' }}
        onClick={handleClick}
        data-testid={testId}
        data-edit-path={editPath}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        <div className={`${styles.cardCheckbox} ${styles.cardCheckboxRight}`}>
          <CheckBox toggleMark={item.checkbox ? { ...item.checkbox, checked: selected } : undefined} data-testid={testId ? `${testId}-checkbox` : undefined} />
        </div>
        <div className={styles.cardLabels} style={columnGapStyle}>
          <Text label={item.topStartLabel} locale={locale} variables={variables} className={styles.cardLabelTop} data-testid={testId ? `${testId}-start-top` : undefined} />
          <Text label={item.bottomStartLabel} locale={locale} variables={variables} className={styles.cardLabelBottom} data-testid={testId ? `${testId}-start-bottom` : undefined} />
        </div>
        {item.badge && (
          <Badge badge={item.badge} locale={locale} className={styles.cardBadge} data-testid={testId ? `${testId}-badge` : undefined} />
        )}
      </div>
    );
  }

  return (
    <div
      className={containerClassName}
      style={containerStyle}
      onClick={handleClick}
      data-testid={testId}
      data-edit-path={editPath}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      {item.image && (
        <div className={styles.image}>
          <Image image={item.image} locale={locale} previewMode={previewMode} data-testid={testId ? `${testId}-image` : undefined} />
        </div>
      )}

      <div className={styles.labelsContainer} style={labelsContainerStyle}>
        <div className={styles.startColumn} style={columnGapStyle}>
          <Text label={item.topStartLabel} locale={locale} variables={variables} className={styles.labelTop} style={labelWrapperStyle} data-testid={testId ? `${testId}-start-top` : undefined} />
          <Text label={item.bottomStartLabel} locale={locale} variables={variables} className={styles.labelBottom} style={labelWrapperStyle} data-testid={testId ? `${testId}-start-bottom` : undefined} />
          {item.descriptionLabel && (
            <Text label={item.descriptionLabel} locale={locale} variables={variables} className={styles.description} data-testid={testId ? `${testId}-description` : undefined} />
          )}
        </div>

        <div className={styles.endColumn} style={columnGapStyle}>
          <Text label={item.topEndLabel} locale={locale} variables={variables} className={styles.labelTop} style={labelWrapperStyle} data-testid={testId ? `${testId}-end-top` : undefined} />
          <Text label={item.bottomEndLabel} locale={locale} variables={variables} className={styles.labelBottom} style={labelWrapperStyle} data-testid={testId ? `${testId}-end-bottom` : undefined} />
        </div>
      </div>

      <div className={styles.checkboxColumn}>
        <CheckBox toggleMark={item.checkbox ? { ...item.checkbox, checked: selected } : undefined} data-testid={testId ? `${testId}-checkbox` : undefined} />
      </div>

      {item.badge && (
        <Badge badge={item.badge} locale={locale} className={styles.badge} data-testid={testId ? `${testId}-badge` : undefined} />
      )}
    </div>
  );
});

LabelsCheckbox.displayName = 'LabelsCheckbox';
